import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

export interface Notification {
  id: string;
  title: string;
  description?: string;
  variant: 'success' | 'error' | 'info' | 'warning';
}

interface UiState {
  sidebarOpen: boolean;
  /** El menú del teléfono (la hamburguesa). No se guarda: al recargar nace cerrado. */
  menuMovilAbierto: boolean;
  theme: Theme;
  notifications: Notification[];
  /** La ventana de Athena. No se guarda: al recargar nace cerrada. */
  athenaAbierta: boolean;

  toggleSidebar: () => void;
  setSidebar: (open: boolean) => void;
  abrirMenuMovil: () => void;
  cerrarMenuMovil: () => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  notify: (n: Omit<Notification, 'id'>) => string;
  dismiss: (id: string) => void;
  abrirAthena: () => void;
  cerrarAthena: () => void;
  alternarAthena: () => void;
}

/** Genera ids sin depender de Date.now()/Math.random a nivel módulo. */
let counter = 0;
const nextId = (): string => `n_${++counter}_${performance.now().toFixed(0)}`;

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      menuMovilAbierto: false,
      theme: 'light',
      notifications: [],
      athenaAbierta: false,

      abrirAthena: () => set({ athenaAbierta: true }),
      cerrarAthena: () => set({ athenaAbierta: false }),
      alternarAthena: () => set((s) => ({ athenaAbierta: !s.athenaAbierta })),

      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebar: (open) => set({ sidebarOpen: open }),
      abrirMenuMovil: () => set({ menuMovilAbierto: true }),
      cerrarMenuMovil: () => set({ menuMovilAbierto: false }),

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      toggleTheme: () => get().setTheme(get().theme === 'light' ? 'dark' : 'light'),

      notify: (n) => {
        const id = nextId();
        set((s) => ({ notifications: [...s.notifications, { ...n, id }] }));
        return id;
      },
      dismiss: (id) =>
        set((s) => ({ notifications: s.notifications.filter((x) => x.id !== id) })),
    }),
    {
      name: 'conversia.ui',
      partialize: (s) => ({ theme: s.theme, sidebarOpen: s.sidebarOpen }),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);

/** Aplica la clase `dark` al <html> para que Tailwind cambie de tema. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}
